import Masonry from 'react-masonry-css';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Checkbox, CircularProgress } from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import DefaultAvatar from '../../static/img/agent-img-3.png';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import PlanAnimation from '../../components/Animations/PlanAnimation.js';

import './index.css';
import agentVoice1Img from '../../static/img/agent-voice-1.png';
import notFoundImg from '../../static/img/not-found.png';

const BoxView = ({ agents = [], loading = false, selectedRows = [], onSelectAgent }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const [isWorkflowOpen, setWorkflowOpen] = useState(false);
  const breakpoints = { default: 3, 991: 2, 700: 1 };

  // Helper function to format relative time
  const getRelativeTime = (date) => {
    if (!date) return 'No activity';
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return past.toLocaleDateString();
  }

  // Map the agents data to match BoxView's expected structure
  const mappedAgents = agents.map((agent, index) => {
    // Get the primary improvement suggestion
    const primarySuggestion = agent['improvementSuggestions']?.find(s => s['priority'] === 'high') || agent['improvementSuggestions']?.[0] || { message: 'No suggestions available', type: 'general' };

    // Format last activity
    let lastActivityText = 'No recent activity';
    if (agent['lastActivity']?.['timestamp']) {
      const timeAgo = getRelativeTime(agent['lastActivity']['timestamp']);
      lastActivityText = `${timeAgo} – ${agent['lastActivity']['message'] || agent['lastActivity']['type']}`;
    }

    return {
      id: agent['_id'] || index,
      name: agent['name'] || 'N/A',
      role: agent['type'] || 'Agent',
      lastActivity: lastActivityText,
      callStats: agent['callStats'] || {},
      phoneNumber: agent['phone'] || 'N/A',
      inboundPhone: agent['inboundPhone'],
      outboundPhone: agent['outboundPhone'],
      profileImage: agent['image'] || DefaultAvatar,
      improvementSuggestion: primarySuggestion['message'],
      voiceAgentName: agent['voiceId']?.['voiceName'] || 'N/A',
      callType: agent['inboundPhone'] ? 'Inbound:' : 'Outbound:',
      improvementSuggestions: agent['improvementSuggestions'] || [],
      salesCalls: `${agent['callStats']?.['failedCalls'] || 0} Failed Calls`,
      status: agent['callStats']?.['totalCalls'] > 0 ? 'active' : 'inactive',
      taskCompleted: `${agent['callStats']?.['totalCalls'] || 0} Total Calls`,
      successfulCalls: `${agent['callStats']?.['successfulCalls'] || 0} Successful`,
      detailText: agent['scriptSummary'] || (agent['script'] ? 'Agent configured for customer interactions' : 'No script configured'),
      tags: agent['tags'] || [],
      voiceAgent: agent['voiceId']?.['voiceImg'] || agentVoice1Img,
      callIcon: <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.998 10.9269C7.48008 10.9269 6.93467 10.8032 6.37091 10.5648C5.82091 10.3311 5.26633 10.0103 4.7255 9.62067C4.18925 9.2265 3.67133 8.7865 3.18091 8.30525C2.69508 7.81484 2.25508 7.29692 1.8655 6.76525C1.47133 6.21525 1.15508 5.66525 0.930498 5.13359C0.692165 4.56525 0.572998 4.01525 0.572998 3.49734C0.572998 3.13984 0.637165 2.80067 0.760915 2.48442C0.889248 2.159 1.0955 1.8565 1.37508 1.59525C1.728 1.24692 2.13133 1.07275 2.56216 1.07275C2.74092 1.07275 2.92425 1.114 3.08008 1.18734C3.25883 1.26984 3.41008 1.39359 3.52008 1.55859L4.58342 3.05734C4.67967 3.19025 4.753 3.31859 4.80341 3.44692C4.863 3.58442 4.89508 3.72192 4.89508 3.85484C4.89508 4.029 4.84467 4.19859 4.74841 4.359C4.67967 4.48275 4.57425 4.61567 4.44133 4.74859L4.12967 5.074C4.13425 5.08775 4.13883 5.09692 4.14341 5.10609C4.19842 5.20234 4.30841 5.36734 4.51925 5.61484C4.74383 5.8715 4.95467 6.10525 5.1655 6.32067C5.43592 6.5865 5.6605 6.79734 5.87133 6.9715C6.13258 7.1915 6.30217 7.3015 6.403 7.35192L6.39383 7.37484L6.72842 7.04484C6.8705 6.90275 7.008 6.79734 7.14092 6.72859C7.393 6.57275 7.71383 6.54525 8.03467 6.67817C8.15383 6.72859 8.28217 6.79734 8.41967 6.89359L9.94133 7.97525C10.1109 8.08984 10.2347 8.2365 10.308 8.41067C10.3767 8.58484 10.4088 8.74525 10.4088 8.90567C10.4088 9.12567 10.3584 9.34567 10.2622 9.55192C10.1659 9.75817 10.0467 9.93692 9.8955 10.1019C9.63425 10.3907 9.35008 10.5969 9.02008 10.7298C8.70383 10.8582 8.36008 10.9269 7.998 10.9269ZM2.56216 1.76025C2.31008 1.76025 2.07633 1.87025 1.85175 2.09025C1.64091 2.28734 1.49425 2.50275 1.40258 2.7365C1.30633 2.97484 1.2605 3.22692 1.2605 3.49734C1.2605 3.92359 1.36133 4.3865 1.563 4.86317C1.76925 5.349 2.058 5.85317 2.42466 6.35734C2.79133 6.8615 3.20842 7.35192 3.66675 7.81484C4.12508 8.26859 4.62008 8.69025 5.12883 9.0615C5.62383 9.42359 6.13258 9.71692 6.63675 9.92775C7.4205 10.2623 8.15383 10.3403 8.75883 10.0882C8.99258 9.99192 9.19883 9.84525 9.38675 9.63442C9.49217 9.51984 9.57467 9.39609 9.64342 9.24942C9.69842 9.13484 9.72591 9.01567 9.72591 8.8965C9.72591 8.82317 9.71217 8.74984 9.6755 8.66734C9.66175 8.63984 9.63425 8.58942 9.54717 8.52984L8.0255 7.44817C7.93383 7.384 7.85133 7.33817 7.77342 7.30609C7.67258 7.26484 7.63133 7.22359 7.4755 7.31984C7.38383 7.36567 7.30133 7.43442 7.20967 7.52609L6.86133 7.86984C6.68258 8.044 6.40758 8.08525 6.19675 8.00734L6.073 7.95234C5.88508 7.8515 5.66508 7.69567 5.42216 7.48942C5.20217 7.3015 4.96383 7.0815 4.67508 6.79734C4.4505 6.56817 4.22591 6.32525 3.99217 6.05484C3.77675 5.80275 3.62091 5.58734 3.52466 5.40859L3.46967 5.27109C3.44217 5.16567 3.433 5.10609 3.433 5.04192C3.433 4.87692 3.49258 4.73025 3.60716 4.61567L3.95091 4.25817C4.04258 4.1665 4.11133 4.07942 4.15716 4.0015C4.19383 3.94192 4.20758 3.8915 4.20758 3.84567C4.20758 3.809 4.19383 3.754 4.17092 3.699C4.13883 3.62567 4.08841 3.54317 4.02425 3.45609L2.96091 1.95275C2.91508 1.88859 2.86008 1.84275 2.79133 1.81067C2.718 1.77859 2.64008 1.76025 2.56216 1.76025ZM6.39383 7.37942L6.3205 7.69109L6.44425 7.37025C6.42133 7.36567 6.403 7.37025 6.39383 7.37942Z" fill="#1E48B4" /><path d="M9.62505 4.41875H7.42505C7.23713 4.41875 7.0813 4.26292 7.0813 4.075V1.875C7.0813 1.68708 7.23713 1.53125 7.42505 1.53125C7.61297 1.53125 7.7688 1.68708 7.7688 1.875V3.73125H9.62505C9.81297 3.73125 9.9688 3.88708 9.9688 4.075C9.9688 4.26292 9.81297 4.41875 9.62505 4.41875Z" fill="#1E48B4" /></svg>,
      respondBox: [
        {
          className: 'last-activity',
          icon: '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 11.375C3.035 11.375 0.625 8.965 0.625 6C0.625 3.035 3.035 0.625 6 0.625C8.965 0.625 11.375 3.035 11.375 6C11.375 8.965 8.965 11.375 6 11.375ZM6 1.375C3.45 1.375 1.375 3.45 1.375 6C1.375 8.55 3.45 10.625 6 10.625C8.55 10.625 10.625 8.55 10.625 6C10.625 3.45 8.55 1.375 6 1.375Z" fill="#2E2C9A" /><path d="M7.85531 7.96488C7.79031 7.96488 7.72531 7.94988 7.66531 7.90988L6.11531 6.98488C5.73031 6.75488 5.44531 6.24988 5.44531 5.80488V3.75488C5.44531 3.54988 5.61531 3.37988 5.82031 3.37988C6.02531 3.37988 6.19531 3.54988 6.19531 3.75488V5.80488C6.19531 5.98488 6.34531 6.24988 6.50031 6.33988L8.05031 7.26488C8.23031 7.36988 8.28531 7.59988 8.18031 7.77988C8.10531 7.89988 7.98031 7.96488 7.85531 7.96488Z" fill="#2E2C9A" /></svg>',
          activityHeading: 'Last Activity - ',
          activityText: lastActivityText
        },
        {
          className: 'suggestions-activity',
          icon: `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="5.99992" cy="5.99992" r="4.99091" stroke="${primarySuggestion['priority'] === 'high' ? '#dc3545' : primarySuggestion['priority'] === 'medium' ? '#ffc107' : '#28a745'}" stroke-width="0.763636"/><circle cx="5.99974" cy="5.99999" r="2.60303" stroke="${primarySuggestion['priority'] === 'high' ? '#dc3545' : primarySuggestion['priority'] === 'medium' ? '#ffc107' : '#28a745'}" stroke-width="0.763636"/></svg>`,
          activityHeading: 'Improvement Suggestions - ',
          activityText: primarySuggestion['message']
        },
      ],
    };
  });

  const AgentCard = ({ agent }) => {
    const handleCardClick = () => {
      console.log("Card clicked");
      document.body.classList.add("hide-layout"); // 👈 class add ki

      history.push(getWorkspacePath(`/agents/${agent['id']}`, currentWorkspace?.['_id']));
    };
    const isSelected = selectedRows.some(row => row['_id'] === agent['id']);

    const handleCheckboxClick = (e) => {
      e.stopPropagation();
      if (onSelectAgent) onSelectAgent(agent['id']);
    };

    return (
      <div className="agent-card" style={{ backgroundColor: agent['background'], cursor: 'pointer' }} onClick={handleCardClick}>
        <div className="agent-header">
          <div className={`checkbox-right-area`}>
            <Checkbox
              checked={isSelected}
              onClick={handleCheckboxClick}
            />
          </div>
          <div className='img-box'>
            <img src={agent['profileImage']} alt="profile" className="agent-image" />
            <div className='status-icon'>
              {agent['status'] === 'active' ? (
                <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="7.66162" cy="8" r="7.3125" fill="#16A249" />
                  <path d="M7.66748 3.93726C5.42376 3.93726 3.60498 5.75603 3.60498 7.99975C3.60498 10.2435 5.42376 12.0622 7.66748 12.0622C9.73122 12.0622 11.4635 10.5152 11.7048 8.48237C11.7308 8.25934 11.5719 8.05175 11.3489 8.02494C11.1263 7.99894 10.9183 8.15778 10.8923 8.38081C10.6993 10.0058 9.31848 11.2497 7.66748 11.2497C5.87267 11.2497 4.41748 9.79456 4.41748 7.99975C4.41748 6.20494 5.87267 4.74975 7.66748 4.74975C8.04245 4.74975 8.41376 4.8176 8.75948 4.94028C8.97073 5.01503 9.19213 4.89763 9.26729 4.68638C9.34204 4.47472 9.23722 4.24073 9.02597 4.16557C8.59372 4.01242 8.13507 3.93726 7.66748 3.93726ZM11.3237 4.74975C11.2197 4.74975 11.1109 4.78632 11.0316 4.86391L7.48954 8.35522C7.38513 8.458 7.27991 8.4381 7.19785 8.31703L6.7916 7.72025C6.66729 7.53704 6.40648 7.48382 6.22042 7.6061C6.03395 7.72838 5.98154 7.98147 6.10586 8.16469L6.5121 8.76147C6.87814 9.30137 7.59435 9.37287 8.06113 8.91381L11.6158 5.4355C11.7743 5.2795 11.7743 5.01991 11.6158 4.86391C11.5366 4.78591 11.4277 4.74975 11.3237 4.74975Z" fill="white" />
                </svg>
              ) : (
                <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="7.66162" cy="8" r="7.3125" fill="#A1A1A1" />
                  <path d="M8.41772 3.6333C8.46263 3.63331 8.50475 3.64596 8.54272 3.6665C8.54711 3.66883 8.55113 3.67175 8.55542 3.67432C8.57794 3.68811 8.59969 3.70352 8.61792 3.72314L11.6414 6.97314C11.6441 6.97605 11.6456 6.9799 11.6482 6.98291C11.6623 6.99936 11.6748 7.01722 11.6853 7.03662C11.6878 7.0414 11.6899 7.04636 11.6921 7.05127C11.7119 7.09267 11.7244 7.1388 11.7244 7.18799V9.21924C11.7244 11.4252 10.8476 12.3677 8.79565 12.3677H6.52808C4.47609 12.3677 3.59937 11.4252 3.59937 9.21924V6.78174C3.59937 4.57583 4.47609 3.63332 6.52808 3.6333H8.41772ZM6.52808 4.24268C4.78598 4.24269 4.16577 4.90895 4.16577 6.78174V9.21924C4.16577 11.092 4.78598 11.7583 6.52808 11.7583H8.79565C10.5378 11.7583 11.158 11.092 11.158 9.21924V7.49268H9.92944C8.637 7.49268 8.13452 6.95236 8.13452 5.56299V4.24268H6.52808ZM8.70093 5.56299C8.70093 6.61111 8.95444 6.8833 9.92944 6.8833H10.7566L8.70093 4.67334V5.56299Z" fill="white" />
                </svg>
              )}
            </div>
          </div>
          <div className="agent-info">
            <h5 className="agent-role">{agent['role']}</h5>
            <h4 className="agent-name">{agent['name']}</h4>
            <div className="agent-detail">{agent['detailText']}</div>
          </div>
          <div className='tags-area'>
            {agent['inboundPhone'] && (
              <div className='tags-box number-box'>
                <span className='icon'>
                  {agent['callIcon']}
                </span>
                <p>Inbound: <span>{agent['inboundPhone']}</span></p>
              </div>
            )}
            {agent['outboundPhone'] && (
              <div className='tags-box number-box'>
                <span className='icon'>
                  {agent['callIcon']}
                </span>
                <p>Outbound: <span>{agent['outboundPhone']}</span></p>
              </div>
            )}
            <div className='tags-box agent-box'>
              <span className='icon'>
                <img src={agent['voiceAgent']} alt="profile" className="agent-image" />
              </span>
              <p>Voice <span>{agent['voiceAgentName']}</span></p>
            </div>
            <div className='tags-box calls-box'>
              <p>{agent['salesCalls']}</p>
            </div>
            <div className='tags-box task-box'>
              <p>{agent['taskCompleted']}</p>
            </div>
            <div className='dynamic-tags-wrapper' style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              flexGrow: 1,
              minWidth: 0
            }}>
              {agent['tags'].map((tag) => {
                const tagName = typeof tag === 'string' ? tag : tag.name;
                const tagColor = typeof tag === 'string' ? '#6B7280' : (tag.color || '#6B7280'); // Default gray

                return (
                  <div key={tagName} className='tags-box' style={{
                    backgroundColor: `${tagColor}80`, // 20% opacity for background
                    color: tagColor,
                    border: `1px solid ${tagColor}100` // 40% opacity for border
                  }}>
                    <p> Tag: {tagName} </p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className='social-box'>
            {/* 👇 Add social icons here */}
            {agent['socials'] && (
              <ul className="agent-socials">
                {agent['socials'].map((social, index) => (
                  <li key={index}>
                    <Link to="#" href={social['link']} target="_blank" rel="noopener noreferrer" title={social['name']}>{social['icon']}</Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {agent['respondBox'] && (
          <div className={`respond-box-area`}>
            {
              agent['respondBox'].map((respond, index) => (
                <div className={`respond-box ${respond['className']}`} key={index}>
                  <div className="icon" dangerouslySetInnerHTML={{ __html: respond['icon'] }}>
                    {/* {respond.icon}
                     */}
                  </div>
                  <div className='text-bx'>
                    <p>{respond['activityHeading']} <span>{respond['activityText']}</span></p>
                  </div>
                </div>
              ))
            }
          </div>
        )}
        {agent['lastSeen'] && (
          <div className='fotter-card-area'>
            <p>{agent['lastActivity']}</p>
            <button className='msg-btn'>
              <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.54983 15.7415C6.48317 15.7415 6.41651 15.7331 6.34984 15.7248C5.84984 15.6581 5.42484 15.3415 5.21651 14.8915L4.1665 12.5831C2.49983 11.3498 1.5415 9.50814 1.5415 7.50814C1.5415 3.9498 4.62484 1.0498 8.4165 1.0498C11.0915 1.0498 13.5332 2.51646 14.6415 4.79146C15.0665 5.63313 15.2915 6.5498 15.2915 7.50814C15.2915 10.9748 12.3665 13.8165 8.7165 13.9581L7.66651 15.2248C7.38317 15.5498 6.97483 15.7415 6.54983 15.7415ZM8.4165 2.29146C5.3165 2.29146 2.7915 4.6248 2.7915 7.4998C2.7915 9.14146 3.60816 10.6581 5.02483 11.6581C5.1165 11.7248 5.1915 11.8081 5.23316 11.9081L6.34984 14.3665C6.39151 14.4581 6.47484 14.4831 6.5165 14.4831C6.55817 14.4915 6.64151 14.4831 6.69984 14.4165L7.93317 12.9331C8.04984 12.7915 8.22484 12.7081 8.4165 12.7081C11.5165 12.7081 14.0415 10.3748 14.0415 7.4998C14.0415 6.74146 13.8665 6.01644 13.5248 5.34144C12.6248 3.48311 10.6165 2.29146 8.4165 2.29146Z" fill="#4B5563" />
                <path d="M14.4499 18.9586C14.0166 18.9586 13.6166 18.7669 13.3332 18.4336L12.2832 17.1752C10.2666 17.0919 8.36657 16.1669 7.1499 14.6669C6.95823 14.4336 6.96658 14.1003 7.15824 13.8753L7.93324 12.9419C8.0499 12.8003 8.2249 12.7169 8.41657 12.7169C11.5166 12.7169 14.0416 10.3836 14.0416 7.50859C14.0416 6.75026 13.8666 6.02524 13.5249 5.35024C13.4166 5.13357 13.4416 4.87525 13.5916 4.68358C13.7416 4.49192 13.9916 4.40857 14.2249 4.45857C17.3082 5.1669 19.4582 7.74192 19.4582 10.7252C19.4582 12.7252 18.4999 14.5669 16.8332 15.8003L15.7832 18.1086C15.5666 18.5752 15.1416 18.8919 14.6332 18.9502C14.5749 18.9586 14.5082 18.9586 14.4499 18.9586ZM8.46657 14.2502C9.50824 15.3086 11.0082 15.9252 12.5832 15.9252C12.7666 15.9252 12.9416 16.0086 13.0666 16.1503L14.2999 17.6336C14.3582 17.7086 14.4332 17.7169 14.4832 17.7086C14.5166 17.7086 14.6082 17.6836 14.6582 17.5836L15.7749 15.1253C15.8249 15.0253 15.8916 14.9336 15.9832 14.8752C17.3999 13.8752 18.2166 12.3586 18.2166 10.7169C18.2166 8.71691 16.9916 6.94194 15.1249 6.06694C15.2416 6.53361 15.2999 7.00859 15.2999 7.50025C15.2999 10.9669 12.3749 13.8086 8.7249 13.9502L8.46657 14.2502Z" fill="#4B5563" />
                <path d="M10.4999 8.125H6.33325C5.99159 8.125 5.70825 7.84167 5.70825 7.5C5.70825 7.15833 5.99159 6.875 6.33325 6.875H10.4999C10.8416 6.875 11.1249 7.15833 11.1249 7.5C11.1249 7.84167 10.8416 8.125 10.4999 8.125Z" fill="#4B5563" />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  };


  return (
    <div className='agent-viewbox-main'>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 'calc(100vh - 200px)'}}>
          <PlanAnimation />
        </div>
      ) : agents.length === 0 ? (
        <div className='notfound-area' style={{ textAlign: 'center', padding: '40px' }}>
          <div className='img-box'>
            <img alt="..." src={notFoundImg} />
          </div>
          <p>No agents found</p>
        </div>
      ) : (
        <div className="agent-container">
          <Masonry
            breakpointCols={breakpoints}
            className="masonry-grid"
            columnClassName="masonry-column"
          >
            {mappedAgents.map((agent) => (
              <AgentCard key={agent['id']} agent={agent} />
            ))}
          </Masonry>
        </div>
      )}
    </div>
  )
}

export default BoxView